import React, { useEffect, useState } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useLang } from '../i18n/LangContext';

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const onAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/${hash}`;
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const links = [
    { id: 'about', href: '#about', label: t('nav.about') },
    { id: 'divisions', href: '#divisions', label: t('nav.divisions') },
    { id: 'projects', href: '#projects', label: t('nav.experience') },
    { id: 'industries', href: '#industries', label: t('nav.industries') },
    { id: 'workforce', href: '#workforce', label: t('nav.workforce') },
    { id: 'gallery', href: '#gallery', label: t('nav.gallery') },
    { id: 'contact', href: '#contact', label: t('nav.contact') },
  ];

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
          scrolled ? 'bg-[#0A0A0B]/85 backdrop-blur-md border-b border-[#27272A]' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="navbar-home-link" className="flex items-center gap-3 group">
            <Logo size={34} />
            <span className="hidden xl:flex flex-col leading-none">
              <span className="font-display text-base tracking-[0.04em] text-[#F5F5F2]/95 group-hover:text-white transition-colors">
                ALPHA <span className="text-[#C8102E]">7</span>
              </span>
              <span className="mt-1 font-mono text-[9px] tracking-[0.24em] uppercase text-[#A1A1AA] group-hover:text-[#F5F5F2] transition-colors">
                Soluciones y Proyectos
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={(e) => onAnchor(e, l.href)}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden lg:flex items-center gap-1 border border-[#27272A] px-2 py-1.5 font-mono text-[11px] tracking-widest">
              <Globe size={12} className="text-[#A1A1AA] mr-1" />
              <button
                data-testid="lang-switch-en"
                onClick={() => setLang('en')}
                className={`px-2 py-0.5 transition-colors ${lang === 'en' ? 'text-white bg-[#C8102E]' : 'text-[#A1A1AA] hover:text-white'}`}
              >EN</button>
              <button
                data-testid="lang-switch-es"
                onClick={() => setLang('es')}
                className={`px-2 py-0.5 transition-colors ${lang === 'es' ? 'text-white bg-[#C8102E]' : 'text-[#A1A1AA] hover:text-white'}`}
              >ES</button>
            </div>

            <a
              href="#contact"
              onClick={(e) => onAnchor(e, '#contact')}
              data-testid="navbar-cta-button"
              className="!hidden lg:!inline-flex btn-primary whitespace-nowrap"
            >
              {t('nav.cta')}
              <span className="arrow">→</span>
            </a>

            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 border border-[#27272A]"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-30 bg-[#0A0A0B] transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="h-full flex flex-col pt-[96px] px-6">
          <nav className="flex-1 overflow-y-auto">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={(e) => onAnchor(e, l.href)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="mobile-link"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="py-8 flex items-center justify-between border-t border-[#27272A]">
            <div className="flex items-center gap-1 border border-[#27272A] px-2 py-1.5 font-mono text-[11px] tracking-widest">
              <Globe size={12} className="text-[#A1A1AA] mr-1" />
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLang('en')}
                className={`px-2 py-0.5 transition-colors ${lang === 'en' ? 'text-white bg-[#C8102E]' : 'text-[#A1A1AA]'}`}
              >EN</button>
              <button
                data-testid="mobile-lang-es"
                onClick={() => setLang('es')}
                className={`px-2 py-0.5 transition-colors ${lang === 'es' ? 'text-white bg-[#C8102E]' : 'text-[#A1A1AA]'}`}
              >ES</button>
            </div>
            <a
              href="#contact"
              onClick={(e) => onAnchor(e, '#contact')}
              data-testid="mobile-cta-button"
              className="btn-primary"
            >
              {t('nav.cta')}
              <span className="arrow">→</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
